import type {
  NavigationType,
  onNavigate,
  beforeNavigate,
  afterNavigate,
  AfterNavigate,
  BeforeNavigate,
  OnNavigate,
  NavigationTarget,
  NavigationState,
} from "@kestrel/navigator";
import type { RefreshOptions, KestrelStores, ServerResult } from "@kestrel/stores";
import { withResolvers } from "@otto-ec/assets-core-utils/promise";
import { getLogger } from "@kestrel/client";
import { get } from "svelte/store";
import { base } from "@kestrel/path";

import type { MaybePromise } from "../types/utils";
import { fetchDataFromServer } from "./fetch/fetch";
import { decodeActionResultFromResponse } from "./fetch/decoder";
import { dataJsonKey, dataJsonValue, navigationTarget, notStartsWithBase } from "./utils";

export const onNavigateHooks = new Set<Parameters<typeof onNavigate>[0]>();
export const beforeNavigateHooks = new Set<Parameters<typeof beforeNavigate>[0]>();
export const afterNavigateHooks = new Set<Parameters<typeof afterNavigate>[0]>();

/**
 *
 *
 *
 *
 *
 */
type UpdateCallback = (stores: KestrelStores, serverResult: ServerResult) => MaybePromise<void>;

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export async function goto(
  stores: KestrelStores,
  options: RefreshOptions,
  to: NavigationTarget,
  type: NavigationType,
  update: UpdateCallback,
): Promise<NavigationState> {
  const { routeId, routePath } = stores;
  const log = getLogger("navigator");

  const currentRouteId = get(routeId);
  const currentRoutePath = get(routePath);

  /*                                                                   */
  const from = navigationTarget(currentRouteId, currentRoutePath, options.base);

  /*                                              */
  const [complete, resolveComplete, rejectComplete] = withResolvers();
  /*                                                          */
  complete.catch(() => {});

  const navigation = {
    to,
    complete,
    from,
    canceled: false as boolean,
    cancel: () => {
      navigation.canceled = true;
      rejectComplete(new Error("Navigation cancelled"));
    },
    type,
    info: options.info,
    result: undefined as ServerResult | undefined,
  } satisfies OnNavigate | BeforeNavigate | AfterNavigate;

  /*                                                                    */
  if (notStartsWithBase(to.url)) {
    log.info("Invalid href:", to.url.href, "does not start with base:", base);
    rejectComplete(new Error("invalid base path"));
    return navigation;
  }

  /*                                           */
  await Promise.all([...beforeNavigateHooks.values()].map((hook) => hook(navigation)));

  /*                                                                   */
  if (navigation.canceled) return navigation;

  /**
 *
 *
 */
  let result: ServerResult;

  /*                                                           */
  /*                                        */
  try {
    log.info("Fetch data for:", to.url.href);
    const dataUrl = new URL(to.url);
    dataUrl.searchParams.set(dataJsonKey, dataJsonValue);
    const response = await fetchDataFromServer(dataUrl, options.init);
    result = await decodeActionResultFromResponse(response);
  } catch (e) {
    const err = e as Error;

    /*                                        */
    result = {
      routeId: currentRouteId!,
      routePath: currentRoutePath,
      error: {
        status: 0,
        title: err.message,
        /*                                                           */
        detail: ((err.cause as Error) ?? e).stack,
        type: "network_error",
      },
      type: "error",
    };
  }

  /*                                                      */
  to.routeId = result.routeId;
  navigation.result = result;

  /*                                                                            */
  /*                                                                        */
  const afterNavFromOnNavigate = (
    await Promise.all([...onNavigateHooks.values()].map((hook) => hook(navigation as OnNavigate)))
  ).filter((v) => typeof v === "function");

  /*                                                                   */
  if (navigation.canceled) {
    rejectComplete(new Error("Navigation cancelled"));
    return navigation;
  }

  /*                                                                    */
  /*                                    */
  if (result.type === "redirect") {
    resolveComplete();
    return navigation;
  }

  /*                                               */
  if (result.type === "error") {
    await update(stores, result);
    rejectComplete(new Error("Navigation resulted in error"));
    return navigation;
  }

  /*                                          */
  await update(stores, result);
  resolveComplete();

  /*                                          */
  await complete;

  /*                                                   */
  await Promise.all(
    [...afterNavFromOnNavigate, ...afterNavigateHooks.values()].map((hook) =>
      hook(navigation as AfterNavigate),
    ),
  );

  return navigation;
}
